import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Paper,
  IconButton,
  InputLabel,
  MenuItem,
  TextField,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MergeIcon from '@mui/icons-material/Merge';

import { useTheme } from '@mui/material/styles';

import { showConfirmDialogComponent } from '../core/ConfirmDialog';
import { useAlert } from '../context/AlertContext';
import {
  useCreateLegalClientFromSubmission,
  useMergeTabletSubmission,
  useRejectTabletSubmission,
} from '../hooks/reactQuerys/TabletSubmissionQueries';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { ITabletSubmission } from '../Interfaces/ITabletSubmission';
import { submissionClientId } from './tabletSubmissionGrouping';
import { DocumentInlinePreview } from '../Clients/DocumentTab/DocumentInlinePreview';
import {
  compareExpiry,
  documentTypeFromPayload,
  expiryComparisonText,
  ID_DOC_TYPE_LABEL,
  storedIdDocument,
  versionCaption,
} from './storedIdDocument';
import {
  EXPIRY_WARN_DAYS,
  photosRemovedInfo,
  photosRemovedText,
  rejectConfirmText,
  sourceLabel,
} from './tabletSubmissionLabels';

interface Props {
  open: boolean;
  onClose: () => void;
  submission: ITabletSubmission | null;
  participants: ILegalClient[];
}

type ExpiryStatus = 'valid' | 'expiring_soon' | 'expired';
interface ExpiryCheck {
  status: ExpiryStatus;
  days: number;
  formattedDate: string;
}

function checkDocumentExpiry(
  expiry: string | undefined,
  warnDays = EXPIRY_WARN_DAYS
): ExpiryCheck | null {
  if (!expiry) return null;
  const exp = new Date(expiry);
  if (Number.isNaN(exp.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  exp.setHours(0, 0, 0, 0);
  const days = Math.floor(
    (exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  const formattedDate = exp.toLocaleDateString('de-DE');
  if (days < 0) return { status: 'expired', days, formattedDate };
  if (days <= warnDays) return { status: 'expiring_soon', days, formattedDate };
  return { status: 'valid', days, formattedDate };
}

interface PersonPayload {
  FirstName?: string;
  LastName?: string;
  BirthDate?: string;
  BirthName?: string;
  BirthPlace?: string;
  Nationality?: string;
  DocumentNumber?: string;
  DocumentExpiry?: string;
}
interface AddressPayload {
  Street?: string;
  HouseNumber?: string;
  Zip?: string;
  City?: string;
  Country?: string;
}
interface ContactPayload {
  Phone?: string;
  Email?: string;
}
interface IdScanPayload {
  Method?: 'nfc' | 'ocr' | 'manual' | 'foto';
  /** Dokumentart der Erfassung (#1299) — steuert den Dokumenttyp der Ablage. */
  DocumentType?: string;
  FrontPhotoBase64?: string;
  BackPhotoBase64?: string;
  // Optionales Selfie-Foto vom Mandanten — eingeführt im Tablet-Service-
  // Branch (``f820f3c3 "Foto für kreußlein"``). Das Feld ist hier
  // bewusst typisiert, damit der TSX-Code in BEIDEN Branches kompiliert:
  // ohne Selfie-UI (z. B. ``1019-hbci-underkonten``) bleibt das Feld
  // ungenutzt; mit Selfie-UI (z. B. ``1013-dino-tablet-service``/``testing``)
  // kann es ausgelesen werden.
  SelfiePhotoBase64?: string;
  // Vermerk aus _stripIdScanPhotos: die Fotos wurden planmäßig entfernt
  // (übernommen/abgelehnt/abgebrochen) — siehe #1273.
  PhotosRemoved?: unknown;
}
interface FullPayload {
  Person?: PersonPayload;
  Address?: AddressPayload;
  Contact?: ContactPayload;
  IdScan?: IdScanPayload;
}

const PERSON_LABEL: Record<keyof PersonPayload, string> = {
  FirstName: 'Vorname',
  LastName: 'Nachname',
  BirthDate: 'Geburtsdatum',
  BirthName: 'Geburtsname',
  BirthPlace: 'Geburtsort',
  Nationality: 'Staatsangehörigkeit',
  DocumentNumber: 'Ausweis-Nr.',
  DocumentExpiry: 'Gültig bis',
};

const ADDRESS_LABEL: Record<keyof AddressPayload, string> = {
  Street: 'Straße',
  HouseNumber: 'Hausnummer',
  Zip: 'PLZ',
  City: 'Ort',
  Country: 'Land',
};

const CONTACT_LABEL: Record<keyof ContactPayload, string> = {
  Phone: 'Telefon',
  Email: 'E-Mail',
};

const METHOD_LABEL: Record<NonNullable<IdScanPayload['Method']>, string> = {
  nfc: 'NFC (eID)',
  ocr: 'OCR aus Foto',
  manual: 'Manuelle Eingabe',
  foto: 'Foto + manuelle Eingabe',
};

function _changed(
  prefill: string | undefined,
  value: string | undefined
): boolean {
  const a = (prefill ?? '').trim();
  const b = (value ?? '').trim();
  return a !== b && b.length > 0;
}

export const TabletSubmissionDetailDialog: React.FC<Props> = ({
  open,
  onClose,
  submission,
  participants,
}) => {
  const { showAlert } = useAlert();
  const theme = useTheme();
  const { mergeIntoLegalClient, isPendingMergeIntoLegalClient } =
    useMergeTabletSubmission();
  const { createLegalClientFromSubmission, isPendingCreateLegalClient } =
    useCreateLegalClientFromSubmission();
  const { rejectSubmission, isPendingRejectSubmission } =
    useRejectTabletSubmission();

  const [photoZoom, setPhotoZoom] = useState<string | null>(null);
  // submissionClientId, weil das Backend den Fremdschlüssel als
  // `idLegalClient` liefert (Relations-JOIN-Alias) — mit
  // `LegalClient_idLegalClient` war die Vorbelegung immer leer, obwohl die
  // Submission in der DB an einen Beteiligten gebunden ist.
  const [targetIdLegalClient, setTargetIdLegalClient] = useState<number | ''>(
    submissionClientId(submission) ?? ''
  );

  const payload: FullPayload = useMemo(() => {
    if (!submission?.Payload) return {};
    if (typeof submission.Payload === 'object') {
      return submission.Payload as FullPayload;
    }
    try {
      return JSON.parse(String(submission.Payload)) as FullPayload;
    } catch {
      return {};
    }
  }, [submission?.Payload]);

  const prefill: FullPayload = useMemo(() => {
    if (!submission?.PrefillPayload) return {};
    if (typeof submission.PrefillPayload === 'object') {
      return submission.PrefillPayload as FullPayload;
    }
    try {
      return JSON.parse(String(submission.PrefillPayload)) as FullPayload;
    } catch {
      return {};
    }
  }, [submission?.PrefillPayload]);

  const boundIdLegalClient = submissionClientId(submission);
  React.useEffect(() => {
    if (open) {
      setTargetIdLegalClient(boundIdLegalClient ?? '');
    }
  }, [open, boundIdLegalClient]);

  // Dokumentart der Ablage (#1299): Vorschlag aus der Erfassung, korrigierbar —
  // der Mandant kann sich verklickt haben, und die Ablage ist der
  // Identifizierungsnachweis.
  //
  // Muss VOR dem frühen Return stehen: Hooks hinter einer Bedingung ändern die
  // Hook-Zahl zwischen zwei Renderdurchläufen, sobald der Dialog von „keine
  // Erfassung" auf eine Erfassung wechselt.
  const submittedDocumentType = documentTypeFromPayload(
    payload.IdScan?.DocumentType
  );
  const [idDocumentType, setIdDocumentType] = useState<number>(
    submittedDocumentType
  );
  React.useEffect(() => {
    setIdDocumentType(submittedDocumentType);
  }, [submittedDocumentType, submission?.idTabletSubmission]);

  // Ablaufdatum: Vorschlag aus der Erfassung, hier korrigierbar. Im Portal
  // tippt es der Mandant (optional, bleibt oft leer), am Tablet kommt es aus
  // der MRZ. Der Mitarbeiter hat das Ausweisfoto daneben und ist die Instanz,
  // die es ohnehin prüfen muss.
  const submittedExpiry = (payload.Person?.DocumentExpiry ?? '').slice(0, 10);
  const [validUntil, setValidUntil] = useState<string>(submittedExpiry);
  React.useEffect(() => {
    setValidUntil(submittedExpiry);
  }, [submittedExpiry, submission?.idTabletSubmission]);

  if (!submission) return null;

  const idScan = payload.IdScan ?? {};
  const methodLabel =
    METHOD_LABEL[idScan.Method ?? 'manual'] ?? 'Manuelle Eingabe';
  const hasFront = !!idScan.FrontPhotoBase64;
  const hasBack = !!idScan.BackPhotoBase64;
  const hasSelfie = !!idScan.SelfiePhotoBase64;
  const photosRemoved = photosRemovedInfo(submission);
  // Zuletzt hinterlegter Ausweis desselben Beteiligten (#1298). Die Beteiligten
  // kommen aus dem Vorgangs-fullObject und tragen ihre Dokument-Versionen schon
  // mit — es braucht keinen zusätzlichen Abruf.
  const boundParticipant = participants.find(
    p =>
      String(p.idLegalClient) === String(submissionClientId(submission) ?? '')
  );
  // Verglichen wird nur mit derselben Dokumentart — ein Personalausweis gegen
  // einen Reisepass ist keine Verlängerung (#1299).
  const stored = storedIdDocument(boundParticipant, idDocumentType);
  const expiryComparison = stored
    ? compareExpiry(validUntil, stored.version.ValidUntil)
    : null;
  const expiryNote = expiryComparison
    ? expiryComparisonText(expiryComparison)
    : null;
  // Beide Reihen tragen dieselbe Art Bildunterschrift, damit sich die
  // Ablaufdaten ohne Suchen vergleichen lassen.
  const submissionCaption = submittedExpiry
    ? `gültig bis ${new Date(submittedExpiry).toLocaleDateString('de-DE')}`
    : 'ohne Ablaufdatum';
  const storedCaption = stored
    ? [
        versionCaption(stored),
        stored.version.ValidUntil
          ? `gültig bis ${new Date(
              stored.version.ValidUntil
            ).toLocaleDateString('de-DE')}`
          : 'ohne Ablaufdatum',
        stored.version.Note,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
  const isStatusSubmitted = submission.Status === 'submitted';
  // Warnung und Vergleich richten sich nach dem, was übernommen wird — sonst
  // widerspricht die Anzeige dem Feld direkt darüber.
  const expiryInfo = checkDocumentExpiry(validUntil);

  async function handleCreateNew() {
    const confirmed = await showConfirmDialogComponent(
      'Neuen Beteiligten anlegen',
      'Aus diesen Daten wird ein neuer Beteiligter angelegt und dem Vorgang ' +
        'hinzugefügt. Vorhandene Beteiligte bleiben unberührt.',
      'Anlegen',
      'Abbrechen',
      theme
    );
    if (!confirmed) return;
    try {
      const result = (await createLegalClientFromSubmission(
        submission!.idTabletSubmission
      )) as
        | {
            createdLegalClient?: { FirstName?: string; LastName?: string };
            warning?: string | null;
          }
        | undefined;
      const lc = result?.createdLegalClient;
      const name = [lc?.FirstName, lc?.LastName].filter(Boolean).join(' ');
      showAlert({
        text: name
          ? `Neuer Beteiligter "${name}" wurde dem Vorgang hinzugefügt.`
          : 'Neuer Beteiligter wurde dem Vorgang hinzugefügt.',
        severity: 'success',
      });
      if (result?.warning) {
        showAlert({ text: result.warning, severity: 'warning' });
      }
      onClose();
    } catch {
      showAlert({
        text: 'Beteiligten anlegen fehlgeschlagen.',
        severity: 'error',
      });
    }
  }

  async function handleMerge() {
    if (!targetIdLegalClient) {
      showAlert({
        text: 'Bitte einen Beteiligten auswählen.',
        severity: 'warning',
      });
      return;
    }
    const target = participants.find(
      p => p.idLegalClient === targetIdLegalClient
    );
    const targetName =
      [target?.FirstName, target?.LastName].filter(Boolean).join(' ') ||
      `Beteiligter #${targetIdLegalClient}`;
    const confirmed = await showConfirmDialogComponent(
      'Daten übernehmen',
      `Die erfassten Daten werden in „${targetName}" übernommen. Bestehende ` +
        'Werte werden ergänzt bzw. überschrieben, Ausweisfotos als Dokument ' +
        'am Beteiligten abgelegt.',
      'Übernehmen',
      'Abbrechen',
      theme
    );
    if (!confirmed) return;
    try {
      const result = (await mergeIntoLegalClient({
        idTabletSubmission: submission!.idTabletSubmission,
        idLegalClient: Number(targetIdLegalClient),
        idLegalClientDocumentType: idDocumentType,
        ValidUntil: validUntil || undefined,
      })) as
        | {
            merge?: {
              merged: boolean;
              fields: string[];
              warning?: string | null;
            };
          }
        | undefined;
      const merge = result?.merge;
      if (merge?.merged && merge.fields.length > 0) {
        showAlert({
          text: `Daten in "${targetName}" übernommen: ${merge.fields.join(', ')}.`,
          severity: 'success',
        });
      } else {
        showAlert({
          text: `Daten in "${targetName}" übernommen.`,
          severity: 'success',
        });
      }
      if (merge?.warning) {
        showAlert({ text: merge.warning, severity: 'warning' });
      }
      onClose();
    } catch {
      showAlert({ text: 'Übernahme fehlgeschlagen.', severity: 'error' });
    }
  }

  async function handleReject() {
    const confirmed = await showConfirmDialogComponent(
      'Erfassung ablehnen',
      rejectConfirmText(submission),
      'Ablehnen',
      'Abbrechen',
      theme
    );
    if (!confirmed) return;
    try {
      await rejectSubmission(submission!.idTabletSubmission);
      showAlert({ text: 'Eingabe abgelehnt.', severity: 'info' });
      onClose();
    } catch {
      showAlert({ text: 'Ablehnung fehlgeschlagen.', severity: 'error' });
    }
  }

  function renderFieldRows<T extends Record<string, string>>(
    labelMap: T,
    data: Partial<Record<keyof T, string | undefined>>,
    prefillData: Partial<Record<keyof T, string | undefined>>
  ) {
    return (Object.keys(labelMap) as Array<keyof T>)
      .filter(
        k =>
          (data[k] ?? '').toString().trim() !== '' ||
          (prefillData[k] ?? '').toString().trim() !== ''
      )
      .map(k => {
        const newVal = (data[k] ?? '').toString();
        const oldVal = (prefillData[k] ?? '').toString();
        const isChanged = _changed(oldVal, newVal);
        return (
          <TableRow key={String(k)}>
            <TableCell sx={{ color: 'text.secondary', width: '40%' }}>
              {labelMap[k]}
            </TableCell>
            <TableCell>
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ flexWrap: 'wrap' }}
              >
                <Typography variant='body2'>{newVal || '–'}</Typography>
                {isChanged && (
                  <Tooltip
                    title={
                      oldVal
                        ? `Vorbelegt: "${oldVal}"`
                        : 'Vom Mandanten ergänzt'
                    }
                  >
                    <Chip
                      label={oldVal ? 'geändert' : 'ergänzt'}
                      size='small'
                      color={oldVal ? 'warning' : 'info'}
                    />
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          </TableRow>
        );
      });
  }

  const isMutating =
    isPendingMergeIntoLegalClient ||
    isPendingCreateLegalClient ||
    isPendingRejectSubmission;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>
          Erfassung prüfen
          <Typography
            variant='caption'
            display='block'
            color='text.secondary'
            sx={{ mt: 0.5 }}
          >
            Eingang #{submission.idTabletSubmission} · {sourceLabel(submission)}{' '}
            · Methode: {methodLabel}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {photosRemoved && !hasFront && !hasBack && !hasSelfie && (
              <Alert severity='info'>
                {photosRemovedText(
                  photosRemoved,
                  ID_DOC_TYPE_LABEL[idDocumentType] ?? 'Ausweisdokument'
                )}
              </Alert>
            )}

            {/* Ein Block statt zwei: die beiden Reihen gehoeren zusammen, denn
                der Sinn ist der Vergleich. Vorher standen sie als getrennte
                Abschnitte untereinander, in unterschiedlicher Kachelgroesse und
                mit unterschiedlichem Zuschnitt — man musste erst lesen, welche
                Reihe welche ist. */}
            {(hasFront || hasBack || hasSelfie || stored) && (
              <Paper variant='outlined' sx={{ p: 2 }}>
                <Typography variant='subtitle1'>
                  {ID_DOC_TYPE_LABEL[idDocumentType] ?? 'Ausweisdokument'}
                </Typography>

                {isStatusSubmitted && (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{ mt: 2 }}
                  >
                    <TextField
                      select
                      size='small'
                      label='Dokumentart der Ablage'
                      value={idDocumentType}
                      onChange={e => setIdDocumentType(Number(e.target.value))}
                      helperText={
                        idDocumentType === submittedDocumentType
                          ? 'Aus der Erfassung übernommen — bei Bedarf korrigieren.'
                          : `Abweichend von der Erfassung (${
                              ID_DOC_TYPE_LABEL[submittedDocumentType]
                            }).`
                      }
                      sx={{ minWidth: 320 }}
                    >
                      {Object.keys(ID_DOC_TYPE_LABEL).map(id => (
                        <MenuItem key={id} value={Number(id)}>
                          {ID_DOC_TYPE_LABEL[Number(id)]}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      type='date'
                      size='small'
                      label='Gültig bis'
                      value={validUntil}
                      onChange={e => setValidUntil(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      helperText={
                        submittedExpiry
                          ? validUntil === submittedExpiry
                            ? 'Aus der Erfassung übernommen — bei Bedarf korrigieren.'
                            : `Abweichend von der Erfassung (${new Date(
                                submittedExpiry
                              ).toLocaleDateString('de-DE')}).`
                          : 'Die Erfassung enthält kein Ablaufdatum — bitte vom Ausweis ablesen.'
                      }
                      sx={{ minWidth: 260 }}
                    />
                  </Stack>
                )}

                {expiryNote && (
                  <Alert severity={expiryNote.severity} sx={{ mt: 2 }}>
                    {expiryNote.text}
                  </Alert>
                )}

                {(hasFront || hasBack || hasSelfie) && (
                  <Box sx={{ mt: 2 }}>
                    <PhotoRowLabel
                      title='Neu eingereicht'
                      accent
                      caption={submissionCaption}
                    />
                    <PhotoRow>
                      {hasFront && (
                        <PhotoThumb
                          src={idScan.FrontPhotoBase64!}
                          label='Vorderseite'
                          onZoom={setPhotoZoom}
                        />
                      )}
                      {hasBack && (
                        <PhotoThumb
                          src={idScan.BackPhotoBase64!}
                          label='Rückseite'
                          onZoom={setPhotoZoom}
                        />
                      )}
                      {hasSelfie && (
                        <PhotoThumb
                          src={idScan.SelfiePhotoBase64!}
                          label='Foto der Person'
                          onZoom={setPhotoZoom}
                        />
                      )}
                    </PhotoRow>
                  </Box>
                )}

                {(hasFront || hasBack || hasSelfie) && stored && (
                  <Divider sx={{ mt: 2 }} />
                )}

                {stored && (
                  <Box sx={{ mt: 2 }}>
                    <PhotoRowLabel
                      title='Bereits hinterlegt'
                      caption={storedCaption}
                    />
                    <PhotoRow>
                      {stored.files.map(file => (
                        <Box key={file.idDocument} sx={{ width: TILE_WIDTH }}>
                          <Typography variant='caption' color='text.secondary'>
                            {file.Label ?? file.FileName}
                          </Typography>
                          <DocumentInlinePreview
                            idDocument={file.idDocument}
                            fileName={file.FileName}
                            height={TILE_HEIGHT}
                            // Ein Ausweis ist auf Kachelgroesse nicht lesbar.
                            // Der Klick oeffnet dieselbe Grossansicht wie bei
                            // den Fotos der Erfassung — sonst zeigt der
                            // Vergleich zwar, DASS ein Ausweis hinterlegt ist,
                            // aber nicht, welcher.
                            onZoom={setPhotoZoom}
                          />
                        </Box>
                      ))}
                    </PhotoRow>
                  </Box>
                )}
              </Paper>
            )}

            <Box>
              <Typography variant='subtitle1' gutterBottom>
                Person
              </Typography>
              {expiryInfo && expiryInfo.status !== 'valid' && (
                <Alert
                  severity={
                    expiryInfo.status === 'expired' ? 'error' : 'warning'
                  }
                  sx={{ mb: 1 }}
                >
                  {expiryInfo.status === 'expired'
                    ? `Personalausweis ist seit ${Math.abs(expiryInfo.days)} Tagen abgelaufen (gültig bis ${expiryInfo.formattedDate}). Bitte vor der Beurkundung einen gültigen Ausweis anfordern.`
                    : `Personalausweis läuft in ${expiryInfo.days} Tagen ab (gültig bis ${expiryInfo.formattedDate}).`}
                </Alert>
              )}
              <Table size='small'>
                <TableBody>
                  {renderFieldRows(
                    PERSON_LABEL,
                    (payload.Person ?? {}) as Record<
                      string,
                      string | undefined
                    >,
                    (prefill.Person ?? {}) as Record<string, string | undefined>
                  )}
                </TableBody>
              </Table>
            </Box>

            <Box>
              <Typography variant='subtitle1' gutterBottom>
                Anschrift
              </Typography>
              <Table size='small'>
                <TableBody>
                  {renderFieldRows(
                    ADDRESS_LABEL,
                    (payload.Address ?? {}) as Record<
                      string,
                      string | undefined
                    >,
                    (prefill.Address ?? {}) as Record<
                      string,
                      string | undefined
                    >
                  )}
                </TableBody>
              </Table>
            </Box>

            <Box>
              <Typography variant='subtitle1' gutterBottom>
                Kontakt
              </Typography>
              <Table size='small'>
                <TableBody>
                  {renderFieldRows(
                    CONTACT_LABEL,
                    (payload.Contact ?? {}) as Record<
                      string,
                      string | undefined
                    >,
                    (prefill.Contact ?? {}) as Record<
                      string,
                      string | undefined
                    >
                  )}
                </TableBody>
              </Table>
            </Box>

            {isStatusSubmitted && (
              <>
                <Divider />
                <Box>
                  <Typography variant='subtitle1' gutterBottom>
                    In bestehenden Beteiligten übernehmen
                  </Typography>
                  <FormControl fullWidth size='small'>
                    <InputLabel id='merge-target-label'>Beteiligter</InputLabel>
                    <Select
                      labelId='merge-target-label'
                      label='Beteiligter'
                      value={targetIdLegalClient}
                      onChange={e =>
                        setTargetIdLegalClient(
                          e.target.value === '' ? '' : Number(e.target.value)
                        )
                      }
                    >
                      <MenuItem value=''>
                        <em>– bitte wählen –</em>
                      </MenuItem>
                      {participants.map(p => (
                        <MenuItem key={p.idLegalClient} value={p.idLegalClient}>
                          {[p.FirstName, p.LastName]
                            .filter(Boolean)
                            .join(' ') || `Beteiligter #${p.idLegalClient}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}

            {!isStatusSubmitted && (
              <Alert severity='info'>
                Diese Eingabe ist bereits abgeschlossen (Status:{' '}
                {submission.Status}). Daten werden nur zur Ansicht angezeigt.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Schließen</Button>
          {isStatusSubmitted && (
            <>
              <Button
                color='error'
                onClick={handleReject}
                disabled={isMutating}
              >
                Ablehnen
              </Button>
              <Button
                variant='outlined'
                startIcon={<PersonAddIcon />}
                onClick={handleCreateNew}
                disabled={isMutating}
              >
                Als neuen Beteiligten
              </Button>
              <Button
                variant='contained'
                startIcon={<MergeIcon />}
                onClick={handleMerge}
                disabled={isMutating || !targetIdLegalClient}
              >
                Übernehmen
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!photoZoom}
        onClose={() => setPhotoZoom(null)}
        maxWidth='lg'
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
            onClick={() => setPhotoZoom(null)}
          >
            <CloseIcon />
          </IconButton>
          {photoZoom && (
            <img
              src={photoZoom}
              alt='Ausweis'
              style={{ display: 'block', maxWidth: '100%', maxHeight: '85vh' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Kachelmass für beide Reihen. Gleiche Grösse und gleicher Zuschnitt sind die
 *  Voraussetzung dafür, dass der Vergleich einer ist — vorher war die
 *  Erfassung 200 px breit und beschnitten, die Ablage 220 px und eingepasst. */
export const TILE_WIDTH = 220;
export const TILE_HEIGHT = 140;

/** Überschrift einer Foto-Reihe. `accent` hebt die aktuelle Erfassung ab; die
 *  Ablage bleibt ruhig, damit der Blick zuerst auf dem Neuen landet. */
const PhotoRowLabel: React.FC<{
  title: string;
  caption?: string;
  accent?: boolean;
}> = ({ title, caption, accent }) => (
  <Stack
    direction='row'
    spacing={1}
    alignItems='baseline'
    flexWrap='wrap'
    sx={{ mb: 1 }}
  >
    <Typography
      variant='subtitle2'
      color={accent ? 'primary.main' : 'text.secondary'}
    >
      {title}
    </Typography>
    {caption && (
      <Typography variant='caption' color='text.secondary'>
        {caption}
      </Typography>
    )}
  </Stack>
);

const PhotoRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Stack direction='row' spacing={2} sx={{ overflowX: 'auto', pb: 0.5 }}>
    {children}
  </Stack>
);

const PhotoThumb: React.FC<{
  src: string;
  label: string;
  onZoom: (src: string) => void;
}> = ({ src, label, onZoom }) => (
  <Box sx={{ width: TILE_WIDTH, flexShrink: 0 }}>
    <Typography variant='caption' color='text.secondary'>
      {label}
    </Typography>
    <Box
      sx={{
        position: 'relative',
        cursor: 'zoom-in',
        '&:hover .zoom-hint': { opacity: 1 },
      }}
      onClick={() => onZoom(src)}
    >
      <img
        src={src}
        alt={label}
        style={{
          display: 'block',
          width: '100%',
          height: TILE_HEIGHT,
          // `contain`, nicht `cover`: bei einem Ausweis darf nichts
          // abgeschnitten werden — die MRZ steht am unteren Rand.
          objectFit: 'contain',
          borderRadius: 4,
          backgroundColor: 'rgba(0,0,0,0.04)',
        }}
      />
      <Box
        className='zoom-hint'
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          bgcolor: 'rgba(0,0,0,0.6)',
          color: 'white',
          borderRadius: 1,
          p: 0.5,
          opacity: 0,
          transition: 'opacity 0.2s',
        }}
      >
        <OpenInFullIcon fontSize='small' />
      </Box>
    </Box>
  </Box>
);
